import React from "react";
import { TextInput, Label } from "Components/TextInput";
import { SvgDelete } from "melodies-source/Svgs/Delete";
import { Body1 } from "melodies-source/Text";
import styled from "styled-components";
import { Entry } from "../index";
import { useMobileMediaQuery } from "hooks";
import { formatNumber } from "Utils/format";

interface Props {
  initialKey: string;
  sourceKey: string;
  sourceLabel: string;
  entries: number;
  onChange: (initialKey: string, property: keyof Entry, value: string) => void;
  onDelete: (key: string) => void;
  error?: Partial<Entry>;
}

export const UtmEntry: React.FC<Props> = ({
  initialKey,
  sourceKey,
  sourceLabel,
  entries,
  onChange,
  onDelete,
  error,
}) => {
  const isMobile = useMobileMediaQuery();
  const deleteButton = entries === 0 && (
    <DeleteIconContainer>
      <SvgDelete onClick={() => onDelete(initialKey)} />
    </DeleteIconContainer>
  );

  return (
    <Container>
      {entries > 0 ? (
        isMobile ? (
          <div>
            <Label>UTM String</Label>
            <SourceKey>{sourceKey}</SourceKey>
          </div>
        ) : (
          <SourceKey>{sourceKey}</SourceKey>
        )
      ) : (
        <TextInput
          label={isMobile && "UTM String"}
          placeholder="Enter UTM String"
          value={sourceKey}
          onChange={(value) =>
            onChange(initialKey, "key", value.toLowerCase().trim())
          }
          hasError={!!error?.key}
          helperText={error?.key}
        />
      )}
      <TextInput
        label={isMobile && "Reporting Label"}
        placeholder="Enter Reporting Label"
        value={sourceLabel}
        onChange={(value) => onChange(initialKey, "label", value)}
        hasError={!!error?.label}
        helperText={error?.label}
      />
      {isMobile ? (
        <EntriesAndDeleteContainerMobile>
          <Body1>Instances: {formatNumber(entries)}</Body1>
          {deleteButton}
        </EntriesAndDeleteContainerMobile>
      ) : (
        <>
          <Body1>{formatNumber(entries)}</Body1>
          {deleteButton}
        </>
      )}
    </Container>
  );
};

const SourceKey = styled(Body1)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Container = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 3fr) 4fr 70px 44px;
  gap: 10px;

  ${SourceKey} {
    font-size: 14px;
    height: 44px;
    align-content: center;
  }

  ${({ theme }) => theme.media.mobile} {
    display: flex;
    flex-direction: column;

    ${SourceKey} {
      height: 30px;
    }

    ${Label} {
      color: var(--text-color);
      margin-bottom: 5px;
    }
  }
`;

const DeleteIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: var(--main-color);
  border-radius: 50%;
  width: 44px;
  height: 44px;

  &:hover {
    background-color: var(--hover-background-color);
  }

  ${({ theme }) => theme.media.mobile} {
    width: 35px;
    height: 35px;
  }
`;

const EntriesAndDeleteContainerMobile = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${Body1} {
    font-weight: 600;
  }
`;
